import stringWidth from 'string-width';

const MAX_PREVIEW_LINES = 5;
const MAX_LINE_LENGTH = 120;

export const truncate = (text: string): string =>
  text
    .split('\n')
    .slice(0, MAX_PREVIEW_LINES)
    .map((line) =>
      line.length <= MAX_LINE_LENGTH ? line : `${line.slice(0, MAX_LINE_LENGTH - '...'.length)}...`,
    )
    .join('\n');

export const isTruncatable = (text: string): boolean => truncate(text).length !== text.length;

function sliceToWidth(text: string, maxWidth: number): string {
  let result = '';
  let width = 0;
  for (const char of text) {
    const charWidth = stringWidth(char);
    if (width + charWidth > maxWidth) break;
    result += char;
    width += charWidth;
  }
  return result;
}

/** Like {@link truncateToWidth}, but always appends the ellipsis, even if `text` already fits — signals "cut off here" rather than "too wide." */
export const forceEllipsis = (text: string, maxWidth: number): string => {
  const w = Math.max(1, maxWidth);
  const ellipsis = '…';
  return `${sliceToWidth(text, Math.max(0, w - stringWidth(ellipsis)))}${ellipsis}`;
};

/** Truncates by rendered width, not `.length` — CJK/emoji are 2 columns wide. */
export const truncateToWidth = (text: string, maxWidth: number): string =>
  stringWidth(text) <= Math.max(1, maxWidth) ? text : forceEllipsis(text, maxWidth);

// Tool outputs are often a stringified JSON object/array on a single line, which
// truncates into an unreadable fragment. Pretty-print those so line-based
// truncation reveals the structure; leave everything else untouched.
export const formatJsonOutput = (text: string): string => {
  try {
    const parsed: unknown = JSON.parse(text.trim());
    if (typeof parsed === 'object' && parsed !== null) {
      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    // not JSON
  }
  return text;
};
